import React, { useContext, useState } from 'react';
import { SnackbarContext } from '../../../../state/SnackbarContextProvider';
import {
  TextField,
  Button,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import useAction from '../../../../utils/useAction';
import { createSchoolAdmin } from '../../../../actions/user.actions';
import { useTranslation } from 'react-i18next';
import { AdminRoleGranted } from '../../../../ampli';
import services from '../../../../services';
import { useSelector } from 'react-redux';
import { selectSelectedSchoolId } from '../../../../reducers/user.selectors';

interface AddAdminProps {
  onAdminAdded: () => void;
  open: boolean;
  onClose: () => void;
}

const AddAdmin: React.FC<AddAdminProps> = ({ onAdminAdded, open, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const createSchoolAdminAction = useAction(createSchoolAdmin);
  const { t } = useTranslation();
  const { openSnackbar } = useContext(SnackbarContext);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);

  const showError = (message:string) => {
    openSnackbar({
      type: 'error',
      message,
    });
  };

  const showInfo = (message:string) => {
    openSnackbar({
      type: 'success',
      message,
    });
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const clearForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: ''
    });
  };

  const handleClose = () => {
    setLoading(false);
    clearForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email) {
      showError(t('All fields are required'));
      return;
    }

    if (!selectedSchoolId) {
      showError(t('No school selected'));
      return;
    }

    // Validate email format
    if (!validateEmail(formData.email)) {
      showError(t('Invalid email address'));
      return;
    }
    setLoading(true);

    try {
      const data = await createSchoolAdminAction(
        formData.firstName,
        formData.lastName,
        formData.email,
        selectedSchoolId
      );

      if (!data.error) {
        showInfo(t('School admin added successfully'));

        if (selectedSchoolId && data.result.userId) {
          services.track.ampliEventTrack({
            event: new AdminRoleGranted({
              'school id': selectedSchoolId,
              'teacher user id': data.result.userId
            })
          });
        }
      } else {
        const message = t(data.error.data.message) ||  t('Error while creating school admin');
        showError(message);
        return;
      }

      // Clear form and notify parent
      clearForm();
      onAdminAdded();
    } catch (err: any) {
      showError(t(err.message) || t('Error while creating school admin'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>{t('Add School Admin')}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin='dense'
          size='small'
          label={t('First Name')}
          type='text'
          value={formData.firstName}
          onChange={handleInputChange('firstName')}
          variant='standard'
          required
          inputProps={{ 'aria-label': 'First Name' }}
        />
        <TextField
          fullWidth
          size='small'
          label={t('Last Name')}
          value={formData.lastName}
          onChange={handleInputChange('lastName')}
          variant='standard'
          required
          inputProps={{ 'aria-label': 'Last Name' }}
        />
        <TextField
          fullWidth
          size='small'
          label={t('Email')}
          type='email'
          value={formData.email}
          onChange={handleInputChange('email')}
          variant='standard'
          required
          inputProps={{ 'aria-label': 'Email' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSubmit}
          startIcon={loading && <CircularProgress size={20} color='inherit' />}
          disabled={loading}
          variant='contained'
          color='success'
        >{t('Submit')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAdmin;
